import { MeasurementsQueryResult } from '../../../timeSeries/types'
import { Measurement } from '@/types/meters'
import HighchartsChart from '@/components/Charts/HighchartsChart'
import Highcharts from 'highcharts'
import { parseFirstMeasurementAggOrValue } from '../utils/parseFirstMeasurementAggOrValue'
import { CommonChartProps, useCommonChartOptions } from '../commonChartOptions'
import minBy from 'lodash/minBy'
import maxBy from 'lodash/maxBy'

export interface TimeSeriesLineChartOptions {
    yMin?: number
    yMax?: number
    minYSpan?: number
}

interface TimeSeriesLineChartProps extends CommonChartProps, TimeSeriesLineChartOptions {
    series: MeasurementsQueryResult[]
    options?: TimeSeriesLineChartOptions & CommonChartProps
}

function getMinMax(series: Measurement[][]): { min?: number; max?: number } {
    const minBySeries = series
        .map((s: Measurement[]) => minBy(s, parseFirstMeasurementAggOrValue))
        .filter((item): item is Measurement => Boolean(item))
    const minMeasurement = minBy(minBySeries, parseFirstMeasurementAggOrValue)
    const maxBySeries = series
        .map((s: Measurement[]) => maxBy(s, parseFirstMeasurementAggOrValue))
        .filter((item): item is Measurement => Boolean(item))
    const maxMeasurement = maxBy(maxBySeries, parseFirstMeasurementAggOrValue)

    return {
        min: minMeasurement && parseFirstMeasurementAggOrValue(minMeasurement),
        max: maxMeasurement && parseFirstMeasurementAggOrValue(maxMeasurement),
    }
}

function computeYSpan(
    series: MeasurementsQueryResult[],
    minYSpan: number | undefined
): { yMin?: number; yMax?: number } {
    if (minYSpan === undefined || minYSpan < 0) {
        return {}
    }
    const { min: seriesMin, max: seriesMax } = getMinMax(series.map((s) => s.data.measurements))
    if (seriesMin === undefined || seriesMax === undefined) {
        return {}
    }
    const seriesSpan = seriesMax - seriesMin
    if (seriesSpan > minYSpan) {
        return {}
    }
    const delta = minYSpan - seriesSpan
    return {
        yMax: seriesMax + delta / 2,
        yMin: seriesMin - delta / 2,
    }
}

const getChartFormatOptions = (
    options: TimeSeriesLineChartProps['options'],
    series: MeasurementsQueryResult[]
): Highcharts.Options => {
    const ySpan = computeYSpan(series, options?.minYSpan)

    return {
        title: {
            text: '',
        },
        chart: {
            backgroundColor: 'transparent',
        },
        yAxis: {
            title: {
                text: '',
            },
            max: ySpan.yMax || options?.yMax,
            min: ySpan.yMin || options?.yMin,
        },
        plotOptions: {
            series: {
                animation: false,
            },
        },
    }
}

const isTimeAggregated = (series: MeasurementsQueryResult[]): boolean => {
    try {
        const firstMeasurementTimestamp = series[0].data.measurements[0].timestamp
        return new Date(firstMeasurementTimestamp).getFullYear() === 1970
    } catch (e) {
        return false
    }
}

const getDateTimeLabelFormats = (series: MeasurementsQueryResult[]): string | undefined => {
    return isTimeAggregated(series) ? '<b>%H:%M</b>' : undefined
}

const timeProfileXAxisOptions = {
    type: 'datetime',
    labels: {
        format: '{value:%H:%M}',
    },
    min: new Date(1970, 0, 1).setHours(0, 0, 0, 0),
    max: new Date(1970, 0, 2).setHours(0, 0, 0, 0), // Set to 24:00
    startOnTick: true,
    endOnTick: true,
}

const LineChart: React.FC<TimeSeriesLineChartProps> = ({ series, disableLegend, options, ...highchartChartProps }) => {
    const isTimeProfile = isTimeAggregated(series)
    const commonOptions = useCommonChartOptions()
    const chartFormatOptions = getChartFormatOptions(options, series)
    return (
        <HighchartsChart
            title="Bar Chart"
            type="bar"
            options={{
                ...commonOptions,
                ...chartFormatOptions,
                tooltip: {
                    pointFormat: `{series.name}: <b>{point.y:.0f} ${series[0].data.unit || ''}</b>`,
                    xDateFormat: getDateTimeLabelFormats(series),
                },
                series: series.map((s) => ({
                    name: s.data.label || s.data.meterName,
                    data: s.data.measurements.map((m) => ({
                        x: m.timestamp,
                        y: parseFirstMeasurementAggOrValue(m),
                    })),
                    type: 'line',
                    color: s.data.color,
                })),
                legend: {
                    enabled: !disableLegend,
                },

                xAxis: isTimeProfile ? timeProfileXAxisOptions : commonOptions.xAxis,
                time: {
                    timezone: series[0].data.timeZone,
                },
            }}
            {...highchartChartProps}
        />
    )
}

export default LineChart
